import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useSetSelectTheme } from "@/contexts/SelectThemeContext";
import { Trash2, AlertCircle, Check, Bookmark, RotateCcw } from "lucide-react";

// iter156.5.3 — tier-aware Cart theme. When the cart tier is Select (R-1617),
// the Cart page + Nav swap to the dark/gold shell so the flow reads as one
// continuous R-1617 experience: PDP → Cart → Checkout. Supply carts continue
// to render in the default bone / oxblood palette. Mirrors the same
// `useSetSelectTheme(isSelectCart)` wire-up Checkout uses (iter156.5.1).
export default function Cart() {
  const {
    items, remove, setQty, saveForLater, saved, moveSavedToCart, removeSaved,
    subtotal, count, MIN_KITS, kitsRemaining, meetsMinimum, hasSingleBuyMix,
    total, retailSubtotal, kitSavings,
    isSelectCart, cartIs10vk,
    FREE_SHIP_THRESHOLD, freeShipping, freeShipRemaining,
  } = useCart();
  const nav = useNavigate();

  // iter160.b — when the cart is empty the `isSelectCart` computed flag flips
  // to false (no lines → no tier), which used to bounce the buyer back to the
  // default Supply-branded shell + /shop link. Preserve the last-viewed tier
  // from localStorage so "Continue browsing" and "Browse the catalog" send
  // the buyer back to the tier they came from, and keep the empty-state
  // palette on-tier too.
  const lastTier = (() => {
    try { return (localStorage.getItem("bbk_last_tier") || "supply").toLowerCase(); }
    catch { return "supply"; }
  })();
  const isSelect = isSelectCart || (items.length === 0 && lastTier === "select");
  const shopHref = isSelect ? "/shop?tier=select" : "/shop";
  useSetSelectTheme(isSelect);

  const theme = isSelect
    ? {
        shell: "bg-[#F1E5C7] text-[#1B1614] min-h-screen",
        eyebrow: "text-[#1B1614]/70 hover:text-[#6E521A]",
        h1: "text-[#1B1614]",
        emptyCard: "bg-[#EBE0BE] border border-[#D6C8A3] rounded-2xl",
        emptyMuted: "text-[#1B1614]/70",
        emptyCTA: "text-[#1B1614] hover:text-[#6E521A]",
        itemCard: "bg-[#EBE0BE] rounded-2xl border border-[#D6C8A3]",
        byokCard: "bg-[#EBE0BE] rounded-2xl border border-[#DDA94E]/40",
        byokAccent: "text-[#6E521A]",
        mutedText: "text-[#1B1614]/70",
        strongText: "text-[#1B1614]",
        subtleMuted: "text-[#1B1614]/55",
        priceLarge: "text-[#1B1614]",
        qtyPill: "bg-[#F1E5C7] border border-[#D6C8A3]",
        qtyBtn: "hover:bg-[#DDA94E] hover:text-black text-[#1B1614]",
        removeBtn: "text-[#1B1614]/60 hover:text-[#6E521A]",
        summaryCard: "bg-[#EBE0BE] rounded-2xl border border-[#D6C8A3]",
        savingsBanner: "border border-[#DDA94E] border-l-4 bg-[#DDA94E]/[0.08]",
        savingsHead: "text-[#6E521A]",
        savingsSub: "text-[#6E521A]/80",
        retailStrike: "text-[#1B1614]/45",
        kitSavingsRow: "text-[#6E521A]",
        hairline: "border-t border-[#D6C8A3] my-5",
        totalText: "text-[#6E521A]",
        totalLabel: "text-[#1B1614]",
        shipCalcNote: "text-[#1B1614]/60",
        shippingLine: "text-[#1B1614]/70",
        freeShipUnlocked: {
          box: "bg-charcoal border-2 border-[#DDA94E] rounded-xl px-4 py-4 mb-4 flex items-center gap-3 shadow-md",
          icon: "text-[#DDA94E]",
          text: "text-bone font-semibold tracking-wide",
        },
        freeShipProgress: {
          box: "bg-[#DDA94E]/[0.06] border border-[#DDA94E]/40 rounded-xl px-4 py-4 mb-4",
          icon: "text-[#6E521A]",
          text: "text-[#1B1614]",
          bold: "text-[#6E521A]",
          track: "bg-[#2A2620]",
          fill: "bg-[#DDA94E]",
        },
        minWarn: {
          box: "bg-[#DDA94E]/[0.06] border border-[#DDA94E]/40 rounded-xl p-4 mb-6 flex items-start gap-3",
          icon: "text-[#6E521A]",
          head: "text-[#6E521A]",
          body: "text-[#1B1614]/80",
          note: "text-[#1B1614]/60",
        },
        minMet: {
          box: "bg-[#1E3A20]/60 border border-[#3F6B41] rounded-xl p-4 mb-6 flex items-center gap-3",
          icon: "text-[#7BC97E]",
          text: "text-[#A6D9A8]",
        },
        cta: "w-full py-[18px] bg-[#DDA94E] text-black tracking-[0.22em] text-[13px] md:text-[14px] font-semibold uppercase hover:bg-[#F1E5C7] transition-colors rounded-full disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#DDA94E]",
        payFineprint: "text-[#1B1614]/60",
        moveBtn: "bg-[#DDA94E] hover:bg-[#F1E5C7] text-black",
        savedPrice: "text-[#6E521A]",
        savedHairline: "border-t border-[#D6C8A3]",
        savedEyebrow: "text-[#1B1614]/70",
      }
    : {
        shell: "",
        eyebrow: "text-stone hover:text-charcoal",
        h1: "text-charcoal",
        emptyCard: "bg-[#EFEAE0] rounded-2xl",
        emptyMuted: "text-stone",
        emptyCTA: "text-charcoal hover:text-oxblood",
        itemCard: "bg-white rounded-2xl border border-borderSubtle",
        byokCard: "bg-white rounded-2xl border border-oxblood/40",
        byokAccent: "text-oxblood",
        mutedText: "text-stone",
        strongText: "text-charcoal",
        subtleMuted: "text-charcoal/60",
        priceLarge: "text-charcoal",
        qtyPill: "bg-bone border border-borderSubtle",
        qtyBtn: "hover:bg-charcoal hover:text-bone text-charcoal",
        removeBtn: "text-stone hover:text-oxblood",
        summaryCard: "bg-white rounded-2xl border border-borderSubtle",
        savingsBanner: "border border-oxblood border-l-4 bg-[#FDF6E3]",
        savingsHead: "text-oxblood",
        savingsSub: "text-oxblood/80",
        retailStrike: "text-stone",
        kitSavingsRow: "text-oxblood",
        hairline: "hairline my-5",
        totalText: "text-charcoal",
        totalLabel: "text-charcoal",
        shipCalcNote: "text-charcoal/60",
        shippingLine: "text-charcoal/70",
        freeShipUnlocked: {
          box: "bg-oxblood border-2 border-oxblood rounded-xl px-4 py-4 mb-4 flex items-center gap-3 shadow-md",
          icon: "text-bone",
          text: "text-bone font-semibold tracking-wide",
        },
        freeShipProgress: {
          box: "bg-[#F5EFE3] border border-[#D8C7A5] rounded-xl px-4 py-4 mb-4",
          icon: "text-[#8A6D1E]",
          text: "text-[#5A4A15]",
          bold: "text-[#5A4A15]",
          track: "bg-[#E8D8B4]",
          fill: "bg-[#8A6D1E]",
        },
        minWarn: {
          box: "bg-[#FFF4D6] border border-[#E8C97A] rounded-xl p-4 mb-6 flex items-start gap-3",
          icon: "text-[#8A6D1E]",
          head: "text-[#5A4A15]",
          body: "text-[#8A6D1E]",
          note: "text-[#8A6D1E]/80",
        },
        minMet: {
          box: "bg-[#EDF7EE] border border-[#B7DCB9] rounded-xl p-4 mb-6 flex items-center gap-3",
          icon: "text-[#2F7A31]",
          text: "text-[#265427]",
        },
        cta: "w-full py-[18px] bg-charcoal text-bone tracking-[0.22em] text-[13px] md:text-[14px] font-semibold uppercase hover:bg-oxblood transition-colors rounded-full disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-charcoal",
        payFineprint: "text-stone",
        moveBtn: "bg-charcoal hover:bg-oxblood text-bone",
        savedPrice: "text-oxblood",
        savedHairline: "border-t border-borderSubtle",
        savedEyebrow: "text-stone",
      };

  return (
    <div className={theme.shell} data-testid={isSelect ? "cart-page-select" : "cart-page-supply"}>
    <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-14 lg:py-20" data-testid="cart-page">
      <Link
        to={shopHref}
        className={`inline-flex items-center gap-2 mb-8 rounded-full border px-5 py-2.5 text-[12px] uppercase tracking-[0.24em] font-semibold transition-colors shadow-sm ${
          isSelect
            ? "border-[#DDA94E] text-[#1B1614] bg-transparent hover:bg-[#DDA94E] hover:text-[#1B1614]"
            : "border-oxblood text-oxblood bg-transparent hover:bg-oxblood hover:text-bone"
        }`}
        data-testid="continue-shopping-link"
      >
        ← Continue Shopping
      </Link>
      <h1 className={`font-serif text-5xl md:text-6xl tracking-tight mb-10 ${theme.h1}`}>Cart</h1>

      {items.length === 0 ? (
        <div className={`p-12 text-center ${theme.emptyCard}`} data-testid="cart-empty">
          <p className={`mb-6 ${theme.emptyMuted}`}>Your cart is empty.</p>
          <Link to={shopHref} className={`text-[13px] uppercase tracking-[0.22em] ${theme.emptyCTA}`} data-testid="empty-cart-cta">
            Browse the catalog →
          </Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-8">
          {/* Line items */}
          <div className="space-y-3">
            {items.map((i) => {
              if (i.is_byok_kit) {
                return (
                  <div
                    key={i.product_id}
                    className={`p-5 flex flex-col gap-3 ${theme.byokCard}`}
                    data-testid={`cart-byok-${i.product_id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className={`text-[12px] uppercase tracking-[0.22em] mb-1 ${theme.byokAccent}`}>Research 1617 · Custom kit</p>
                        <p className={`font-serif text-xl leading-tight ${theme.strongText}`}>Build-Your-Own {i.byok_size}-Vial Kit</p>
                        <p className={`text-xs mt-1 ${theme.mutedText}`}>{Math.round((i.byok_discount_rate || 0) * 100)}% off applied</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className={`font-mono text-lg font-semibold ${theme.priceLarge}`}>${i.price.toFixed(2)}</p>
                        {i.byok_subtotal && i.byok_subtotal > i.price ? (
                          <p className={`text-[13px] font-mono line-through ${theme.retailStrike}`}>${i.byok_subtotal.toFixed(2)}</p>
                        ) : null}
                        {i.byok_discount_amount ? (
                          <p className={`text-[13px] font-mono ${theme.byokAccent}`}>You save ${i.byok_discount_amount.toFixed(2)}</p>
                        ) : null}
                      </div>
                <button
                  onClick={() => remove(i.product_id, i.variant, i.variant_mg)}
                  className={`transition-colors p-2.5 rounded-full ${theme.removeBtn} hover:bg-oxblood/10 focus:bg-oxblood/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-oxblood/40`}
                  data-testid={`cart-remove-byok-${i.product_id}`}
                  aria-label="Remove kit"
                  title="Remove kit"
                >
                  <Trash2 size={18} strokeWidth={1.75}/>
                </button>
                    </div>
                    <ul className={`pt-3 space-y-1.5 ${theme.savedHairline}`}>
                      {(i.byok_items || []).map((bi, idx) => (
                        <li key={`${i.product_id}-${idx}`} className="flex items-center justify-between gap-3 text-sm">
                          <span className={theme.strongText}>1× {bi.name}{bi.variant_mg ? ` ${bi.variant_mg}mg` : ""}</span>
                          <span className={`font-mono text-xs ${theme.mutedText}`}>${Number(bi.price).toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              }
              return (
              <div key={`${i.product_id}::${i.variant || ""}::${i.variant_mg || ""}`} className={`p-6 grid grid-cols-[1fr_auto_auto_auto] gap-6 items-center ${theme.itemCard}`} data-testid={`cart-item-${i.product_id}${i.variant ? "-" + i.variant : ""}${i.variant_mg ? "-" + i.variant_mg + "mg" : ""}`}>
                <div>
                  <p className={`font-serif text-[22px] md:text-[26px] leading-tight ${theme.strongText}`}>{i.name}</p>
                  {(() => {
                    // Normalize the unit-label at display time — variant is the
                    // source of truth. Old cart items stored "5 Vials" style
                    // strings; the buyer wants "5-vial Kit" / "1 vial" instead.
                    const isKit = i.variant === "3vial" || i.variant === "5vial" || i.variant === "10vial";
                    const kitSize = i.variant === "3vial" ? 3 : i.variant === "5vial" ? 5 : i.variant === "10vial" ? 10 : null;
                    // iter185.2 — Supply fixed-tier kits carry kit_vial_count.
                    const isSupplyKit = !i.variant && !i.is_byok_kit && Number(i.kit_vial_count) > 1;
                    const isSingleVial = i.variant === "1vial" || (!i.variant && !i.is_byok_kit && !isSupplyKit);
                    let normalized;
                    if (isKit) {
                      normalized = i.variant_mg ? `${i.variant_mg}mg · ${kitSize}-vial Kit` : `${kitSize}-vial Kit`;
                    } else if (isSupplyKit) {
                      normalized = `${i.kit_vial_count}-vial Kit`;
                    } else if (isSingleVial) {
                      normalized = i.variant_mg ? `${i.variant_mg}mg · 1 vial` : "1 vial";
                    } else {
                      normalized = i.unit_label || "10 vials/kit";
                    }
                    return (
                      <p className={`text-[13px] mt-1.5 ${theme.mutedText}`} data-testid={`cart-item-unit-${i.product_id}${i.variant ? "-" + i.variant : ""}${i.variant_mg ? "-" + i.variant_mg + "mg" : ""}`}>
                        {normalized}
                      </p>
                    );
                  })()}
                  {i.sku && <p className={`hidden text-[12px] font-mono uppercase tracking-widest mt-1 ${theme.mutedText}`}>{i.sku}</p>}
                  <button
                    onClick={() => saveForLater(i.product_id, i.variant, i.variant_mg)}
                    className={`mt-3 inline-flex items-center gap-1.5 text-[13px] uppercase tracking-[0.22em] transition-colors ${theme.removeBtn}`}
                    data-testid={`save-for-later-${i.product_id}${i.variant ? "-" + i.variant : ""}${i.variant_mg ? "-" + i.variant_mg + "mg" : ""}`}
                  >
                    <Bookmark size={12} strokeWidth={1.75}/> Save for later
                  </button>
                </div>
                <div className="flex flex-col items-end">
                  {/* iter207.1 · SMART PDP → SIMPLE CART. Select cart lines
                      no longer surface a quantity stepper — the PDP is the
                      authoritative configurator and the Cart faithfully
                      displays whatever purchase configuration the buyer
                      established there. Buyers change their mind by
                      removing the line (trash) or hitting Continue Shopping.
                      Supply-tier lines keep the stepper — that flow is
                      flat-price and doesn't ladder-decompose. */}
                  {(i.tier || "supply") === "select" && !i.is_byok_kit ? (
                    <div
                      className={`inline-flex items-center px-4 h-10 rounded-full font-mono text-[15px] ${theme.qtyPill} ${theme.strongText}`}
                      data-testid={`cart-qty-static-${i.product_id}${i.variant ? "-" + i.variant : ""}${i.variant_mg ? "-" + i.variant_mg + "mg" : ""}`}
                      aria-label={`Quantity ${i.quantity}`}
                    >
                      ×&nbsp;{i.quantity}
                    </div>
                  ) : (
                    <div className={`flex items-center rounded-full ${theme.qtyPill}`}>
                      <button onClick={() => setQty(i.product_id, i.quantity - 1, i.variant, i.variant_mg)} className={`w-10 h-10 rounded-full transition-colors text-base ${theme.qtyBtn}`} data-testid={`cart-dec-${i.product_id}${i.variant ? "-" + i.variant : ""}${i.variant_mg ? "-" + i.variant_mg + "mg" : ""}`} aria-label="Decrease">−</button>
                      <span className={`w-10 text-center font-mono text-[15px] ${theme.strongText}`}>{i.quantity}</span>
                      <button
                        onClick={() => setQty(i.product_id, i.quantity + 1, i.variant, i.variant_mg)}
                        className={`w-10 h-10 rounded-full transition-colors text-base ${theme.qtyBtn} ${!!(i.max_qty_per_order && i.quantity >= i.max_qty_per_order) ? "opacity-40 hover:bg-transparent" : ""}`}
                        data-testid={`cart-inc-${i.product_id}${i.variant ? "-" + i.variant : ""}${i.variant_mg ? "-" + i.variant_mg + "mg" : ""}`}
                        aria-label="Increase"
                      >+</button>
                    </div>
                  )}
                  {i.max_qty_per_order ? (
                    <p className={`text-[12px] uppercase tracking-[0.2em] font-mono mt-1.5 ${theme.mutedText}`} data-testid={`cart-max-hint-${i.product_id}${i.variant ? "-" + i.variant : ""}${i.variant_mg ? "-" + i.variant_mg + "mg" : ""}`}>
                      Limit {i.max_qty_per_order} per order
                    </p>
                  ) : null}
                </div>
                <div className="text-right min-w-[90px]">
                  <p className={`font-mono text-[20px] font-bold ${theme.priceLarge}`}>${(i.price * i.quantity).toFixed(2)}</p>
                  <p className={`text-[13px] font-mono ${theme.mutedText}`}>${i.price.toFixed(2)} ea</p>
                </div>
                <button
                  onClick={() => remove(i.product_id, i.variant, i.variant_mg)}
                  className={`transition-colors p-2.5 rounded-full ${theme.removeBtn} hover:bg-oxblood/10 focus:bg-oxblood/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-oxblood/40`}
                  data-testid={`cart-remove-${i.product_id}${i.variant ? "-" + i.variant : ""}${i.variant_mg ? "-" + i.variant_mg + "mg" : ""}`}
                  aria-label="Remove item"
                  title="Remove item"
                >
                  <Trash2 size={18} strokeWidth={1.75}/>
                </button>
              </div>
              );
            })}
          </div>

          {/* Order summary */}
          <aside>
            <div className={`p-8 sticky top-24 ${theme.summaryCard}`}>
              <h2 className={`font-serif text-[32px] md:text-[36px] mb-7 ${theme.h1}`}>Order Summary</h2>

              {/* Line items — same normalization the cart list uses so
                  the buyer sees identical descriptors in both places. */}
              <div className="mb-5" data-testid="cart-summary-items">
                {items.map((i) => {
                  const kitSize = i.variant === "3vial" ? 3 : i.variant === "5vial" ? 5 : i.variant === "10vial" ? 10 : null;
                  const isSupplyKit = !i.variant && !i.is_byok_kit && Number(i.kit_vial_count) > 1;
                  const isSingleVial = i.variant === "1vial" || (!i.variant && !i.is_byok_kit && !isSupplyKit);
                  let variantLabel;
                  if (i.is_byok_kit) variantLabel = `BYO ${i.byok_size}-vial Kit`;
                  else if (kitSize) variantLabel = i.variant_mg ? `${i.variant_mg}mg · ${kitSize}-vial Kit` : `${kitSize}-vial Kit`;
                  else if (isSupplyKit) variantLabel = `${i.kit_vial_count}-vial Kit`;
                  else if (isSingleVial) variantLabel = i.variant_mg ? `${i.variant_mg}mg · 1 vial` : "1 vial";
                  else variantLabel = i.unit_label || "";
                  return (
                    <div
                      key={`${i.product_id}::${i.variant || ""}::${i.variant_mg || ""}`}
                      className="flex justify-between gap-3 py-2"
                      data-testid={`summary-line-${i.product_id}${i.variant ? "-" + i.variant : ""}${i.variant_mg ? "-" + i.variant_mg + "mg" : ""}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className={`text-[14px] leading-tight truncate ${theme.strongText}`}>{i.name}</div>
                        <div className={`text-[12px] mt-0.5 ${theme.mutedText}`}>{variantLabel} × {i.quantity}</div>
                      </div>
                      <div className={`font-mono text-[14px] whitespace-nowrap ${theme.strongText}`}>
                        ${(i.price * i.quantity).toFixed(2)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className={theme.hairline} />

              {kitSavings > 0.005 && (
                <div
                  className={`mb-6 px-4 py-3 ${theme.savingsBanner}`}
                  data-testid="cart-savings-banner"
                >
                  <p className={`font-serif text-[19px] leading-tight ${theme.savingsHead}`}>
                    You'll save <span className="font-mono font-semibold" data-testid="cart-savings-amount">${kitSavings.toFixed(2)}</span>
                  </p>
                  <p className={`text-[11px] font-mono uppercase tracking-widest mt-1 ${theme.savingsSub}`}>
                    {isSelect ? "Select savings" : "Multi-vial kit discount"}
                  </p>
                </div>
              )}

              {kitSavings > 0.005 && (
                <div className="flex justify-between mb-2 text-[15px]" data-testid="cart-retail">
                  <span className={theme.mutedText}>Retail</span>
                  <span className={`font-mono line-through ${theme.retailStrike}`}>${retailSubtotal.toFixed(2)}</span>
                </div>
              )}
              {kitSavings > 0.005 && (
                <div className={`flex justify-between mb-3 text-[15px] ${theme.kitSavingsRow}`} data-testid="cart-kit-savings">
                  <span>{isSelect ? "Select savings" : "Multi-vial kit savings"}</span>
                  <span className="font-mono">−${kitSavings.toFixed(2)}</span>
                </div>
              )}

              <div className="flex justify-between mb-3 text-[15px]">
                <span className={theme.mutedText}>{(() => {
                  // Truthful breakdown: separate kit lines from single-vial lines
                  // so a mixed cart doesn't misreport "5 kits" when it's really
                  // 4 kits + 1 vial. BYOK kits count as kits.
                  const kits = items.reduce((s, i) => {
                    const isSupplyKit = !i.variant && !i.is_byok_kit && Number(i.kit_vial_count) > 1;
                    const isKit = i.is_byok_kit || i.variant === "3vial" || i.variant === "5vial" || i.variant === "10vial" || isSupplyKit;
                    return s + (isKit ? (i.quantity || 0) : 0);
                  }, 0);
                  const vials = items.reduce((s, i) => {
                    const isSupplyKit = !i.variant && !i.is_byok_kit && Number(i.kit_vial_count) > 1;
                    const isKit = i.is_byok_kit || i.variant === "3vial" || i.variant === "5vial" || i.variant === "10vial" || isSupplyKit;
                    return s + (isKit ? 0 : (i.quantity || 0));
                  }, 0);
                  const parts = [];
                  if (kits > 0) parts.push(`${kits} ${kits === 1 ? "kit" : "kits"}`);
                  if (vials > 0) parts.push(`${vials} ${vials === 1 ? "vial" : "vials"}`);
                  return `Subtotal (${parts.join(" · ") || "0 items"})`;
                })()}</span>
                <span className={`font-mono font-semibold ${theme.strongText}`} data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between mb-3 text-[15px]">
                <span className={theme.mutedText}>Shipping</span>
                <span className={`font-mono font-semibold ${theme.shippingLine}`} data-testid="cart-shipping">
                  <span className={`text-[13px] uppercase tracking-[0.16em] ${theme.shippingLine}`}>Calculated at checkout</span>
                </span>
              </div>
              <div className={theme.hairline} />
              <div className="flex justify-between items-baseline mb-1">
                <span className={`font-serif text-[26px] md:text-[30px] font-medium ${theme.totalLabel}`}>Subtotal</span>
                <span className={`font-mono text-[22px] md:text-[24px] font-bold ${theme.totalText}`} data-testid="cart-total">
                  ${total.toFixed(2)}
                </span>
              </div>
              <p className={`text-[12px] uppercase tracking-[0.16em] mb-7 ${theme.shipCalcNote}`} data-testid="cart-shipping-note">
                + Shipping calculated at checkout
              </p>

              {/* iter221 — Free-shipping progress indicator (reinstated).
                  Hidden for 10VK carts (never free). Shows "Unlocked" or
                  "add $X more" with a visual bar until the threshold hits. */}
              {!cartIs10vk && FREE_SHIP_THRESHOLD != null && items.length > 0 && (
                freeShipping ? (
                  <div className={theme.freeShipUnlocked.box} data-testid="cart-free-ship-unlocked">
                    <Check size={18} strokeWidth={2.5} className={theme.freeShipUnlocked.icon} />
                    <p className={`text-[14px] ${theme.freeShipUnlocked.text}`}>
                      Free USPS Priority Mail or UPS 2nd Day Air unlocked.
                    </p>
                  </div>
                ) : (
                  <div className={theme.freeShipProgress.box} data-testid="cart-free-ship-progress">
                    <p className={`text-[14px] ${theme.freeShipProgress.text}`}>
                      Add <span className={`font-mono font-semibold ${theme.freeShipProgress.bold}`} data-testid="cart-free-ship-remaining">${(freeShipRemaining ?? 0).toFixed(2)}</span> more for FREE USPS Priority Mail or UPS 2nd Day Air.
                    </p>
                    <div className={`mt-2 h-1.5 w-full rounded-full ${theme.freeShipProgress.track}`}>
                      <div
                        className={`h-1.5 rounded-full ${theme.freeShipProgress.fill} transition-all`}
                        style={{ width: `${Math.min(100, (subtotal / FREE_SHIP_THRESHOLD) * 100).toFixed(1)}%` }}
                      />
                    </div>
                  </div>
                )
              )}

              {/* iter185.4 — Minimum-order banner removed (kit minimum is now 1). */}

              <button
                onClick={() => nav("/checkout")}
                disabled={!meetsMinimum}
                className={theme.cta}
                data-testid="checkout-btn"
              >
                Proceed to checkout
              </button>

              {/* Accepted-payment marks — inline real-brand SVG wordmarks
                  self-hosted at /logos/payments/. Row is centered, wraps on
                  narrow viewports, uses only sizing + spacing utilities (no
                  color overrides on the marks so brand colors show through).
                  Understated: 20px logo height (h-5). */}
              <div
                className="flex items-center justify-center flex-nowrap whitespace-nowrap gap-x-2 mt-4 -mx-4"
                aria-label="Accepted payment methods"
                data-testid="checkout-payment-methods"
              >
                <img src="/logos/payments/visa.svg" alt="Visa" className="h-7 w-auto" data-testid="pm-visa" />
                <img src="/logos/payments/mastercard.svg" alt="Mastercard" className="h-7 w-auto" data-testid="pm-mastercard" />
                <img src="/logos/payments/amex.svg" alt="American Express" className="h-7 w-auto" data-testid="pm-amex" />
                <img src="/logos/payments/discover.svg" alt="Discover" className="h-7 w-auto" data-testid="pm-discover" />
                <span className={`text-[13px] font-mono uppercase tracking-[0.22em] leading-none ${theme.payFineprint}`} aria-hidden="true" data-testid="pm-sep-1">|</span>
                <img src="/logos/payments/venmo.svg" alt="Venmo" className="h-7 w-auto" data-testid="pm-venmo" />
                <span className={`text-[13px] font-mono uppercase tracking-[0.22em] leading-none ${theme.payFineprint}`} aria-hidden="true" data-testid="pm-sep-2">|</span>
                <img src="/logos/payments/cashapp.svg" alt="Cash App" className="h-7 w-auto" data-testid="pm-cashapp" />
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Saved for later */}
      {saved.length > 0 && (
        <section className="mt-16" data-testid="saved-for-later-section">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className={`text-[13px] uppercase tracking-[0.22em] mb-1 ${theme.savedEyebrow}`}>Saved for later · {saved.length}</p>
              <h2 className={`font-serif text-3xl md:text-4xl tracking-tight ${theme.h1}`}>Your wishlist</h2>
            </div>
            <p className={`text-xs hidden md:block ${theme.mutedText}`}>Move any kit back to your cart in one tap.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {saved.map((s) => (
              <div key={`${s.product_id}::${s.variant || ""}`} className={`p-5 flex flex-col ${theme.itemCard}`} data-testid={`saved-item-${s.product_id}${s.variant ? "-" + s.variant : ""}`}>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="min-w-0">
                    <p className={`font-serif text-lg leading-tight truncate ${theme.strongText}`}>{s.name}</p>
                    {s.unit_label && <p className={`text-[13px] mt-1 ${theme.mutedText}`} data-testid={`saved-item-unit-${s.product_id}${s.variant ? "-" + s.variant : ""}`}>{s.unit_label}</p>}
                    {s.sku && <p className={`hidden text-[12px] font-mono uppercase tracking-widest mt-1 ${theme.mutedText}`}>{s.sku}</p>}
                  </div>
                  <p className={`font-mono text-sm shrink-0 ${theme.savedPrice}`}>${s.price.toFixed(2)}</p>
                </div>
                <div className={`mt-auto flex items-center gap-2 pt-3 ${theme.savedHairline}`}>
                  <button
                    onClick={() => moveSavedToCart(s.product_id, s.variant)}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 tracking-[0.22em] text-[12px] uppercase rounded-full transition-colors ${theme.moveBtn}`}
                    data-testid={`move-to-cart-${s.product_id}${s.variant ? "-" + s.variant : ""}`}
                  >
                    <RotateCcw size={11} strokeWidth={2}/> Move to cart
                  </button>
                  <button
                    onClick={() => removeSaved(s.product_id, s.variant)}
                    className={`p-2 transition-colors ${theme.removeBtn}`}
                    aria-label="Remove from saved"
                    data-testid={`remove-saved-${s.product_id}${s.variant ? "-" + s.variant : ""}`}
                  >
                    <Trash2 size={14} strokeWidth={1.5}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
    </div>
  );
}
